import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicFetch } from '../utils/authenticatedFetch';
import { ratingFor, colorsFor } from '../shared/sentimentBands';
import SectorHistoryChart from './SectorHistoryChart';

/**
 * Per-sector Fear & Greed breakdown (issue #92).
 *
 * Renders beneath the market-wide gauge so a user can see whether a headline
 * reading is broad-based or is one sector's panic masked by another's euphoria.
 * Scores come from the `fear-greed` worker's /sectors endpoint, which derives
 * them from our own D1 tables.
 *
 * Layout is inline-styled rather than Tailwind: this codebase's prebuilt
 * `tailwind-output.css` is missing several utilities (grid-cols-*, ml-2 ...)
 * and a missing class fails silently. See CLAUDE.md.
 */

const SECTORS_ENDPOINT = 'https://api.1stprinciples.pro/fear-greed/sectors';

/**
 * Matches the sentiment palette already used by the market-wide gauge — `text`
 * and `bg` mirror the `.text-*` / `.bg-*` rules in `src/styles/FearGreed.css`;
 * `bar` is the solid fill those class-based styles have no equivalent for.
 * Kept as inline values on purpose (see the note above about Tailwind).
 */
/** Neutral sits at 50, so bars diverge from the midpoint rather than filling from zero. */
const MIDPOINT = 50;

/**
 * Plain-language restatement of the number behind each component score, so the
 * breakdown explains itself rather than showing five more opaque 0-100 values.
 */
const describeComponent = (key, raw) => {
  const n = (value, digits = 0) => (value == null ? null : value.toFixed(digits));
  switch (key) {
    case 'momentum':
      return raw.above_sma20_pct == null ? null : `${n(raw.above_sma20_pct)}% trade above their 20-day average`;
    case 'strength':
      return raw.range_position == null ? null : `${n(raw.range_position)}% of the way up the 52-week range`;
    case 'highs_lows':
      return raw.near_high_pct == null ? null
        : `${n(raw.near_high_pct, 1)}% near 52-week highs vs ${n(raw.near_low_pct, 1)}% near lows`;
    case 'participation':
      return raw.up_volume_pct == null ? null : `${n(raw.up_volume_pct)}% of volume in advancing stocks`;
    case 'rsi':
      return raw.avg_rsi == null ? null : `average RSI ${n(raw.avg_rsi, 1)}`;
    default:
      return null;
  }
};

/** Signed change vs a prior snapshot, or a dash when no history exists yet. */
const Delta = ({ current, previous, label }) => {
  if (previous == null || current == null) {
    return <span style={{ color: '#9ca3af', fontSize: '0.7rem' }} title={`No ${label} snapshot yet`}>—</span>;
  }
  const change = current - previous;
  const flat = Math.abs(change) < 0.05;
  return (
    <span
      title={`${label}: ${previous.toFixed(1)}`}
      style={{
        fontSize: '0.7rem',
        fontVariantNumeric: 'tabular-nums',
        color: flat ? '#9ca3af' : change > 0 ? '#059669' : '#dc2626'
      }}
    >
      {flat ? '±0.0' : `${change > 0 ? '▲' : '▼'}${Math.abs(change).toFixed(1)}`}
    </span>
  );
};

/** Diverging bar: greed extends right of centre, fear left. */
const DivergingBar = ({ score, color }) => {
  const offset = Math.min(Math.max(score, 0), 100) - MIDPOINT;
  return (
    <div style={{ position: 'relative', height: '14px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: offset >= 0 ? '50%' : `${score}%`,
          width: `${Math.abs(offset)}%`,
          background: color,
          borderRadius: '2px',
          transition: 'width 0.3s ease, left 0.3s ease'
        }}
      />
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px', background: '#d1d5db' }} />
    </div>
  );
};

const ComponentBreakdown = ({ sector, componentMeta }) => (
  <div
    style={{
      padding: '10px 12px',
      background: '#fafafa',
      borderRadius: '6px',
      margin: '2px 0 8px',
      border: '1px solid #f0f0f0'
    }}
  >
    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '8px' }}>
      Equal-weighted from {sector.constituents} liquid {sector.sector} stocks
    </div>
    {Object.entries(componentMeta).map(([key, meta]) => {
      const score = sector.components?.[key];
      if (score == null) return null;
      const detail = describeComponent(key, sector.raw || {});
      // Sub-scores are on the same 0-100 scale as the composite, so they use
      // the same shared bands the worker rates sectors with.
      const colors = colorsFor(ratingFor(score));
      return (
        <div
          key={key}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(90px, 130px) 70px 1fr',
            gap: '10px',
            alignItems: 'center',
            padding: '3px 0'
          }}
          title={meta.description}
        >
          <span style={{ fontSize: '0.72rem', color: '#374151', fontWeight: 500 }}>{meta.label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ flex: 1, height: '5px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${score}%`, height: '100%', background: colors.bar }} />
            </div>
            <span style={{ fontSize: '0.7rem', color: colors.text, fontVariantNumeric: 'tabular-nums', minWidth: '20px' }}>
              {score.toFixed(0)}
            </span>
          </div>
          <span style={{ fontSize: '0.68rem', color: '#6b7280' }}>{detail}</span>
        </div>
      );
    })}
  </div>
);

const SectorRow = ({ sector, componentMeta, expanded, onToggle }) => {
  const colors = colorsFor(sector.rating);
  return (
    <div>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(104px, 168px) 1fr 44px 46px',
          gap: '10px',
          alignItems: 'center',
          padding: '5px 6px',
          borderRadius: '5px',
          cursor: 'pointer',
          background: expanded ? colors.bg : 'transparent'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
          <span style={{ color: '#9ca3af', fontSize: '0.6rem', width: '9px', flexShrink: 0 }}>
            {expanded ? '▼' : '▶'}
          </span>
          <span
            style={{
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {sector.sector}
          </span>
        </span>

        <DivergingBar score={sector.score} color={colors.bar} />

        <span
          style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: colors.text,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {sector.score.toFixed(1)}
        </span>

        <span style={{ textAlign: 'right' }}>
          <Delta current={sector.score} previous={sector.previous_close} label="Previous close" />
        </span>
      </div>

      {expanded && (
        <>
          <div
            style={{
              display: 'flex',
              gap: '14px',
              padding: '2px 6px 6px 24px',
              fontSize: '0.68rem',
              color: '#6b7280'
            }}
          >
            <span style={{ color: colors.text, fontWeight: 600, textTransform: 'capitalize' }}>{sector.rating}</span>
            <span>
              1 week: <Delta current={sector.score} previous={sector.previous_1_week} label="One week ago" />
            </span>
            <span>
              1 month: <Delta current={sector.score} previous={sector.previous_1_month} label="One month ago" />
            </span>
          </div>
          <ComponentBreakdown sector={sector} componentMeta={componentMeta} />
        </>
      )}
    </div>
  );
};

const SectorFearGreedMap = () => {
  const [expandedSector, setExpandedSector] = React.useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sectorFearGreed'],
    queryFn: async () => {
      const response = await publicFetch(SECTORS_ENDPOINT);
      if (!response.ok) throw new Error(`Sector sentiment request failed: ${response.status}`);
      return response.json();
    },
    // Recomputed by the fear-greed cron twice a day; no value in refetching more often.
    staleTime: 60 * 60 * 1000,
    retry: 1
  });

  if (isLoading) {
    return <div style={{ fontSize: '0.78rem', color: '#6b7280', padding: '10px 0' }}>Loading sector sentiment…</div>;
  }

  // The market-wide gauge above is unaffected by a sector failure, so stay quiet.
  if (isError || !data?.sectors?.length) return null;

  const { sectors, sector_average: average, component_meta: componentMeta = {} } = data;
  const mostFearful = sectors[sectors.length - 1];
  const mostGreedy = sectors[0];

  return (
    <div style={{ marginTop: '20px', borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '4px'
        }}
      >
        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Sentiment by Sector</h3>
        {average && (
          <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>
            Sector average{' '}
            <strong style={{ color: colorsFor(average.rating).text }}>{average.score.toFixed(1)}</strong> · {data.as_of}
          </span>
        )}
      </div>

      <p style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '10px' }}>
        {mostGreedy.sector} is the greediest sector and {mostFearful.sector} the most fearful — a{' '}
        {(mostGreedy.score - mostFearful.score).toFixed(0)}-point spread. Click a sector for the components behind its
        score.
      </p>

      <div>
        {sectors.map((sector) => (
          <SectorRow
            key={sector.sector}
            sector={sector}
            componentMeta={componentMeta}
            expanded={expandedSector === sector.sector}
            onToggle={() => setExpandedSector(expandedSector === sector.sector ? null : sector.sector)}
          />
        ))}
      </div>

      <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '10px', lineHeight: 1.5 }}>
        Computed from our own price and technical data across liquid US listings, using the same rating bands as the
        index above. It is a separate measure from the market-wide score and will not match it exactly.
      </div>

      {/* The same scores as a time series (issue #278); price moves are charted in the market heatmap */}
      <SectorHistoryChart metric="sentiment" />
    </div>
  );
};

export default SectorFearGreedMap;
